import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { DURATION_JUMP_TO_BOTTOM_COLLAPSE_MS } from '@/components/ui/motion.ts';

const JUMP_TO_BOTTOM_THRESHOLD_PX = 96;

interface UseJumpToBottomOptions {
	/** The page scroll container, provided explicitly by the owning layout (not discovered via DOM). */
	pageScrollRef: RefObject<HTMLElement | null> | null;
	/** The chat content wrapper, observed for size changes so the button reacts to streaming growth. */
	contentRef: RefObject<HTMLElement | null>;
	enabled: boolean;
	contentVersion: string;
	resetKey: string;
}

export const useJumpToBottom = (options: UseJumpToBottomOptions) => {
	const { pageScrollRef, contentRef, enabled, contentVersion, resetKey } = options;
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasUserScrollIntentRef = useRef(false);
	const [isVisible, setIsVisible] = useState(false);
	const [isCollapsing, setIsCollapsing] = useState(false);

	const updateVisibility = useCallback(() => {
		const scrollContainer = scrollContainerRef.current;
		// TODO: Tune the page-distance calculation so streaming overflow can show the button
		// without requiring prior user scroll intent, while still hiding it during the initial
		// active-answer positioning jump.
		if (!scrollContainer || !enabled || isCollapsing || !hasUserScrollIntentRef.current) {
			setIsVisible(false);
			return;
		}

		const distanceFromBottom =
			scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
		setIsVisible(distanceFromBottom > JUMP_TO_BOTTOM_THRESHOLD_PX);
	}, [enabled, isCollapsing]);

	const markUserScrollIntent = useCallback(() => {
		hasUserScrollIntentRef.current = true;
		requestAnimationFrame(updateVisibility);
	}, [updateVisibility]);

	useEffect(() => {
		const scrollContainer = pageScrollRef?.current;
		if (!scrollContainer) return;

		scrollContainerRef.current = scrollContainer;
		scrollContainer.addEventListener('scroll', updateVisibility, { passive: true });
		scrollContainer.addEventListener('wheel', markUserScrollIntent, { passive: true });
		scrollContainer.addEventListener('touchmove', markUserScrollIntent, { passive: true });
		scrollContainer.addEventListener('keydown', markUserScrollIntent);

		const resizeObserver = new ResizeObserver(updateVisibility);
		resizeObserver.observe(scrollContainer);
		if (contentRef.current) resizeObserver.observe(contentRef.current);

		updateVisibility();

		return () => {
			scrollContainer.removeEventListener('scroll', updateVisibility);
			scrollContainer.removeEventListener('wheel', markUserScrollIntent);
			scrollContainer.removeEventListener('touchmove', markUserScrollIntent);
			scrollContainer.removeEventListener('keydown', markUserScrollIntent);
			resizeObserver.disconnect();
			if (scrollContainerRef.current === scrollContainer) {
				scrollContainerRef.current = null;
			}
		};
	}, [pageScrollRef, contentRef, markUserScrollIntent, updateVisibility]);

	useEffect(() => {
		hasUserScrollIntentRef.current = false;
		setIsVisible(false);
	}, [resetKey]);

	useEffect(() => {
		const frameId = requestAnimationFrame(updateVisibility);
		return () => cancelAnimationFrame(frameId);
	}, [contentVersion, updateVisibility]);

	useEffect(() => {
		return () => {
			if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
		};
	}, []);

	const jumpToBottom = () => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
		setIsCollapsing(true);
		hasUserScrollIntentRef.current = false;
		scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });

		collapseTimerRef.current = setTimeout(() => {
			setIsCollapsing(false);
			setIsVisible(false);
		}, DURATION_JUMP_TO_BOTTOM_COLLAPSE_MS);
	};

	return {
		isVisible: isVisible || isCollapsing,
		isCollapsing,
		jumpToBottom,
	};
};
